class JDProductLightbox {
  constructor(lightbox) {
    this.lightbox = lightbox;

    const galleryId = lightbox.dataset.galleryId;

    this.gallery = galleryId
      ? document.getElementById(galleryId)
      : null;

    this.image = lightbox.querySelector(
      '[data-jd-lightbox-image]'
    );

    this.loader = lightbox.querySelector(
      '[data-jd-lightbox-loader]'
    );

    this.currentDisplay = lightbox.querySelector(
      '[data-jd-lightbox-current]'
    );

    this.totalDisplay = lightbox.querySelector(
      '[data-jd-lightbox-total]'
    );

    this.previousButton = lightbox.querySelector(
      '[data-jd-lightbox-previous]'
    );

    this.nextButton = lightbox.querySelector(
      '[data-jd-lightbox-next]'
    );

    this.closeButton = lightbox.querySelector(
      '[data-jd-lightbox-close]'
    );

    this.backdrop = lightbox.querySelector(
      '[data-jd-lightbox-backdrop]'
    );

    this.triggers = [];
    this.currentIndex = 0;

    this.lastFocusedElement = null;

    this.touchStartX = 0;
    this.touchEndX = 0;

    /*
     * Stores the actual preloaded Image objects.
     *
     * Keeping references prevents them from being discarded
     * and gives us a reliable way to know whether an image
     * has already finished loading.
     */
    this.imageCache = new Map();

    this.handleKeydown =
      this.handleKeydown.bind(this);

    this.handleTouchStart =
      this.handleTouchStart.bind(this);

    this.handleTouchEnd =
      this.handleTouchEnd.bind(this);

    this.init();
  }

  init() {
    if (!this.gallery || !this.image) return;

    this.refreshTriggers();

    /*
     * Start downloading lightbox images only after the
     * regular page has finished loading.
     *
     * This prevents the large lightbox files from competing
     * with the initial product page render.
     */
    if (document.readyState === 'complete') {
      this.scheduleBackgroundPreload();
    } else {
      window.addEventListener(
        'load',
        () => {
          this.scheduleBackgroundPreload();
        },
        { once: true }
      );
    }

    this.gallery.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest(
          '[data-jd-lightbox-trigger]'
        );

        if (
          !trigger ||
          !this.gallery.contains(trigger)
        ) {
          return;
        }

        event.preventDefault();

        this.refreshTriggers();

        const selectedIndex =
          this.triggers.indexOf(trigger);

        this.open(
          selectedIndex >= 0
            ? selectedIndex
            : 0,
          trigger
        );
      }
    );

    this.previousButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.showPrevious();
      }
    );

    this.nextButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.showNext();
      }
    );

    this.closeButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.close();
      }
    );

    this.backdrop?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();

        this.close();
      }
    );

    this.lightbox.addEventListener(
      'touchstart',
      this.handleTouchStart,
      { passive: true }
    );

    this.lightbox.addEventListener(
      'touchend',
      this.handleTouchEnd,
      { passive: true }
    );
  }

  refreshTriggers() {
    this.triggers = Array.from(
      this.gallery.querySelectorAll(
        '[data-jd-lightbox-trigger]'
      )
    ).filter(
      (trigger) =>
        trigger.dataset.fullSrc
    );
  }

  scheduleBackgroundPreload() {
    /*
     * requestIdleCallback lets the browser wait until
     * higher-priority work is finished.
     *
     * Safari does not always support it, so setTimeout
     * acts as the fallback.
     */
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(
        () => {
          this.preloadAllImages();
        },
        {
          timeout: 2000
        }
      );
    } else {
      window.setTimeout(() => {
        this.preloadAllImages();
      }, 750);
    }
  }

  preloadImage(source) {
    if (!source) {
      return Promise.reject(
        new Error('Missing image source.')
      );
    }

    /*
     * If we've already started loading this image,
     * reuse the same Promise instead of requesting
     * it again.
     */
    if (this.imageCache.has(source)) {
      return this.imageCache.get(source);
    }

    const preloadPromise =
      new Promise((resolve, reject) => {
        const preloadedImage =
          new Image();

        /*
         * Hint that this image should be decoded
         * asynchronously where supported.
         */
        preloadedImage.decoding =
          'async';

        preloadedImage.onload =
          async () => {
            /*
             * decode() asks the browser to finish decoding
             * the image before we consider it ready.
             *
             * That helps reduce the pause that can otherwise
             * happen between "downloaded" and "visible."
             */
            try {
              if (preloadedImage.decode) {
                await preloadedImage.decode();
              }
            } catch (error) {
              /*
               * Some browsers may reject decode()
               * even though the image loaded correctly.
               * We still allow the image to display.
               */
            }

            resolve(preloadedImage);
          };

        preloadedImage.onerror =
          () => {
            this.imageCache.delete(
              source
            );

            reject(
              new Error(
                `Unable to preload image: ${source}`
              )
            );
          };

        preloadedImage.src =
          source;
      });

    this.imageCache.set(
      source,
      preloadPromise
    );

    return preloadPromise;
  }

  preloadAllImages() {
    this.refreshTriggers();

    this.triggers.forEach(
      (trigger) => {
        const source =
          trigger.dataset.fullSrc;

        if (!source) return;

        /*
         * Ignore preload failures here.
         *
         * If one fails, showImage() can retry when
         * the customer actually requests it.
         */
        this.preloadImage(source).catch(
          () => {}
        );
      }
    );
  }

  preloadAdjacentImages(index) {
    if (
      this.triggers.length <= 1
    ) {
      return;
    }

    const previousIndex =
      this.normalizeIndex(
        index - 1
      );

    const nextIndex =
      this.normalizeIndex(
        index + 1
      );

    const previousSource =
      this.triggers[
        previousIndex
      ]?.dataset.fullSrc;

    const nextSource =
      this.triggers[
        nextIndex
      ]?.dataset.fullSrc;

    if (previousSource) {
      this.preloadImage(
        previousSource
      ).catch(() => {});
    }

    if (nextSource) {
      this.preloadImage(
        nextSource
      ).catch(() => {});
    }
  }

  open(index, trigger) {
    if (!this.triggers.length) {
      return;
    }

    this.lastFocusedElement =
      trigger;

    this.currentIndex =
      this.normalizeIndex(index);

    this.lightbox.hidden = false;

    this.lightbox.setAttribute(
      'aria-hidden',
      'false'
    );

    document.documentElement.classList.add(
      'jd-lightbox-open'
    );

    document.body.classList.add(
      'jd-lightbox-open'
    );

    document.addEventListener(
      'keydown',
      this.handleKeydown
    );

    this.showImage(
      this.currentIndex
    );

    /*
     * Immediately prioritize the images on either
     * side of the one the customer opened.
     */
    this.preloadAdjacentImages(
      this.currentIndex
    );

    /*
     * Also make sure every remaining image has begun
     * loading.
     */
    this.preloadAllImages();

    window.requestAnimationFrame(
      () => {
        this.closeButton?.focus();
      }
    );
  }

  close() {
    this.lightbox.hidden = true;

    this.lightbox.setAttribute(
      'aria-hidden',
      'true'
    );

    document.documentElement.classList.remove(
      'jd-lightbox-open'
    );

    document.body.classList.remove(
      'jd-lightbox-open'
    );

    document.removeEventListener(
      'keydown',
      this.handleKeydown
    );

    this.lastFocusedElement?.focus();
  }

  async showImage(index) {
    const trigger =
      this.triggers[index];

    if (!trigger) return;

    const source =
      trigger.dataset.fullSrc;

    const altText =
      trigger.dataset.alt || '';

    const totalImages =
      this.triggers.length;

    if (!source) return;

    this.currentIndex = index;

    /*
     * Update the interface immediately.
     */
    if (this.currentDisplay) {
      this.currentDisplay.textContent =
        String(index + 1);
    }

    if (this.totalDisplay) {
      this.totalDisplay.textContent =
        String(totalImages);
    }

    const hideNavigation =
      totalImages <= 1;

    if (this.previousButton) {
      this.previousButton.hidden =
        hideNavigation;
    }

    if (this.nextButton) {
      this.nextButton.hidden =
        hideNavigation;
    }

    /*
     * If the image is still being fetched or decoded,
     * retain the current image rather than instantly
     * blanking the lightbox.
     */
    this.loader?.classList.add(
      'is-visible'
    );

    try {
      await this.preloadImage(
        source
      );

      /*
       * The customer may have clicked another arrow while
       * this image was loading.
       *
       * Don't display an old request after they've already
       * moved somewhere else.
       */
      if (
        this.currentIndex !== index
      ) {
        return;
      }

      this.image.classList.add(
        'is-loading'
      );

      /*
       * Because preloadImage() has already downloaded and
       * decoded the asset, this assignment should come from
       * browser memory/cache rather than starting a new
       * visible download.
       */
      this.image.src = source;
      this.image.alt = altText;

      requestAnimationFrame(() => {
        this.image.classList.remove(
          'is-loading'
        );
      });
    } catch (error) {
      console.error(error);
    } finally {
      if (
        this.currentIndex === index
      ) {
        this.loader?.classList.remove(
          'is-visible'
        );
      }
    }

    /*
     * Every time the customer changes images, prioritize
     * the two most likely images they'll request next.
     */
    this.preloadAdjacentImages(
      index
    );
  }

  showPrevious() {
    if (
      this.triggers.length <= 1
    ) {
      return;
    }

    this.showImage(
      this.normalizeIndex(
        this.currentIndex - 1
      )
    );
  }

  showNext() {
    if (
      this.triggers.length <= 1
    ) {
      return;
    }

    this.showImage(
      this.normalizeIndex(
        this.currentIndex + 1
      )
    );
  }

  normalizeIndex(index) {
    const total =
      this.triggers.length;

    if (total === 0) return 0;

    if (index < 0) {
      return total - 1;
    }

    if (index >= total) {
      return 0;
    }

    return index;
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();

      return;
    }

    if (
      event.key === 'ArrowLeft'
    ) {
      event.preventDefault();
      this.showPrevious();

      return;
    }

    if (
      event.key === 'ArrowRight'
    ) {
      event.preventDefault();
      this.showNext();
    }
  }

  handleTouchStart(event) {
    this.touchStartX =
      event.changedTouches[0]
        ?.screenX || 0;
  }

  handleTouchEnd(event) {
    this.touchEndX =
      event.changedTouches[0]
        ?.screenX || 0;

    const difference =
      this.touchStartX -
      this.touchEndX;

    if (
      Math.abs(difference) < 50
    ) {
      return;
    }

    if (difference > 0) {
      this.showNext();
    } else {
      this.showPrevious();
    }
  }
}

function initializeJDLightboxes(
  root = document
) {
  root
    .querySelectorAll(
      '[data-jd-lightbox]'
    )
    .forEach((lightbox) => {
      if (
        lightbox.dataset
          .jdLightboxInitialized ===
        'true'
      ) {
        return;
      }

      lightbox.dataset
        .jdLightboxInitialized =
        'true';

      new JDProductLightbox(
        lightbox
      );
    });
}

document.addEventListener(
  'DOMContentLoaded',
  () => {
    initializeJDLightboxes();
  }
);

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initializeJDLightboxes(
      event.target
    );
  }
);